'use client';

import type {
  MysteryRoomAccessCardLevel,
  MysteryRoomAccessLevel,
  MysteryRoomAssetFit,
  MysteryRoomAssetTransform,
  MysteryRoomCustomObjectDefinition,
} from './mapTypes';

type MysteryRoomDefaultItemVisual = {
  worldUrl: string;
  gameUrl?: string;
  inventoryUrl: string;
  worldTextureKey: string;
  gameTextureKey?: string;
  inventoryName: string;
  mimeType: string;
  worldDisplay: { width: number; height: number };
};

export type MysteryRoomEffectiveCustomObjectAsset = {
  assetUrl: string;
  assetName?: string;
  assetMimeType?: string;
  assetFit?: MysteryRoomAssetFit;
  assetTransform?: MysteryRoomAssetTransform;
  assetAlphaCollisionEnabled?: boolean;
  textureKey?: string;
  isDefaultItemAsset: boolean;
};

export const MYSTERY_ROOM_GLOCK19_ASSETS = {
  worldUrl: '/games/mystery-room/objects/glock-19-world.png',
  gameUrl: '/games/mystery-room/objects/glock-19-game.png',
  inventoryUrl: '/games/mystery-room/objects/glock-19-invent.png',
  worldTextureKey: 'mr_glock_19_world',
  gameTextureKey: 'mr_glock_19_game',
  inventoryName: 'Glock 19',
  mimeType: 'image/png',
  worldDisplay: { width: 74, height: 56 },
} as const;

export const MYSTERY_ROOM_FLASHLIGHT_ASSETS = {
  worldUrl: '/games/mystery-room/objects/flashlight-world.png',
  inventoryUrl: '/games/mystery-room/objects/flashlight-invent.png',
  worldTextureKey: 'mr_flashlight_world',
  inventoryName: 'Фонарик',
  mimeType: 'image/png',
  worldDisplay: { width: 58, height: 58 },
} as const;

export const MYSTERY_ROOM_ACCESS_CARD_ASSETS = {
  L2: {
    worldUrl: '/games/mystery-room/objects/access-card-l2.webp',
    inventoryUrl: '/games/mystery-room/objects/access-card-l2.webp',
    worldTextureKey: 'mr_access_card_l2_world',
    inventoryName: 'Пропуск L2',
    mimeType: 'image/webp',
    worldDisplay: { width: 62, height: 42 },
  },
  L3: {
    worldUrl: '/games/mystery-room/objects/access-card-l3.webp',
    inventoryUrl: '/games/mystery-room/objects/access-card-l3.webp',
    worldTextureKey: 'mr_access_card_l3_world',
    inventoryName: 'Пропуск L3',
    mimeType: 'image/webp',
    worldDisplay: { width: 62, height: 42 },
  },
  L4: {
    worldUrl: '/games/mystery-room/objects/access-card-l4.webp',
    inventoryUrl: '/games/mystery-room/objects/access-card-l4.webp',
    worldTextureKey: 'mr_access_card_l4_world',
    inventoryName: 'Пропуск L4',
    mimeType: 'image/webp',
    worldDisplay: { width: 62, height: 42 },
  },
  L5: {
    worldUrl: '/games/mystery-room/objects/access-card-l5.webp',
    inventoryUrl: '/games/mystery-room/objects/access-card-l5.webp',
    worldTextureKey: 'mr_access_card_l5_world',
    inventoryName: 'Пропуск L5',
    mimeType: 'image/webp',
    worldDisplay: { width: 62, height: 42 },
  },
} as const;

export const MYSTERY_ROOM_ACCESS_CARD_LEVELS: MysteryRoomAccessCardLevel[] = ['L2', 'L3', 'L4', 'L5'];
export const MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET = MYSTERY_ROOM_ACCESS_CARD_ASSETS.L2;

export function normalizeMysteryRoomAccessCardLevel(value: string | null | undefined): MysteryRoomAccessCardLevel {
  return MYSTERY_ROOM_ACCESS_CARD_LEVELS.includes(value as MysteryRoomAccessCardLevel)
    ? value as MysteryRoomAccessCardLevel
    : 'L2';
}

export function getMysteryRoomAccessLevelForCardLevel(level: MysteryRoomAccessCardLevel): MysteryRoomAccessLevel {
  switch (level) {
    case 'L3':
      return 'server';
    case 'L4':
    case 'L5':
      return 'reactor';
    case 'L2':
    default:
      return 'lab';
  }
}

export function getMysteryRoomAccessCardLevelForAccess(accessLevel: string | null | undefined): MysteryRoomAccessCardLevel {
  switch (accessLevel) {
    case 'server':
      return 'L3';
    case 'reactor':
      return 'L4';
    default:
      return 'L2';
  }
}

export function getMysteryRoomAccessCardVisual(accessLevelOrCardLevel: string | null | undefined): MysteryRoomDefaultItemVisual {
  const cardLevel = MYSTERY_ROOM_ACCESS_CARD_LEVELS.includes(accessLevelOrCardLevel as MysteryRoomAccessCardLevel)
    ? accessLevelOrCardLevel as MysteryRoomAccessCardLevel
    : getMysteryRoomAccessCardLevelForAccess(accessLevelOrCardLevel);
  return MYSTERY_ROOM_ACCESS_CARD_ASSETS[cardLevel];
}

export function getMysteryRoomDefaultItemVisual(itemId: string | null | undefined): MysteryRoomDefaultItemVisual | null {
  switch (itemId) {
    case 'pistol':
      return MYSTERY_ROOM_GLOCK19_ASSETS;
    case 'flashlight':
      return MYSTERY_ROOM_FLASHLIGHT_ASSETS;
    case 'access_card':
      return MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET;
    default:
      return null;
  }
}

export function getMysteryRoomDefaultItemVisualForObject(
  object: Pick<MysteryRoomCustomObjectDefinition, 'itemId' | 'tags' | 'accessCardLevel' | 'grantsAccess'>,
): MysteryRoomDefaultItemVisual | null {
  return (
    getMysteryRoomDefaultItemVisual(object.itemId) ??
    (object.tags?.includes('pistol') ? MYSTERY_ROOM_GLOCK19_ASSETS : null) ??
    (object.tags?.includes('flashlight') ? MYSTERY_ROOM_FLASHLIGHT_ASSETS : null) ??
    (object.tags?.includes('access_card') ? getMysteryRoomAccessCardVisual(object.accessCardLevel ?? object.grantsAccess) : null)
  );
}

export function hasMysteryRoomCustomObjectTextureSkin(
  object: Pick<MysteryRoomCustomObjectDefinition, 'textures'>,
): boolean {
  return (
    object.textures?.some(
      (texture) =>
        texture.visible !== false &&
        Boolean(texture.assetId || texture.assetUrl || texture.builtinTextureId),
    ) ?? false
  );
}

function hasMysteryRoomCustomObjectTextureSource(
  object: Pick<MysteryRoomCustomObjectDefinition, 'textures'>,
): boolean {
  return (
    object.textures?.some((texture) =>
      Boolean(texture.assetId || texture.assetUrl || texture.builtinTextureId),
    ) ?? false
  );
}

export function getMysteryRoomEffectiveCustomObjectAsset(
  object: Pick<
    MysteryRoomCustomObjectDefinition,
    | 'assetUrl'
    | 'assetName'
    | 'assetMimeType'
    | 'assetFit'
    | 'assetTransform'
    | 'assetAlphaCollisionEnabled'
    | 'itemId'
    | 'accessCardLevel'
    | 'grantsAccess'
    | 'tags'
    | 'textures'
  >,
): MysteryRoomEffectiveCustomObjectAsset | null {
  if (hasMysteryRoomCustomObjectTextureSource(object)) {
    return null;
  }

  if (object.assetUrl) {
    return {
      assetUrl: object.assetUrl,
      assetName: object.assetName,
      assetMimeType: object.assetMimeType,
      assetFit: object.assetFit,
      assetTransform: object.assetTransform,
      assetAlphaCollisionEnabled: object.assetAlphaCollisionEnabled,
      isDefaultItemAsset: false,
    };
  }

  const defaultVisual = getMysteryRoomDefaultItemVisualForObject(object);
  if (!defaultVisual) {
    return null;
  }

  return {
    assetUrl: defaultVisual.worldUrl,
    assetName: `${defaultVisual.inventoryName} world`,
    assetMimeType: defaultVisual.mimeType,
    assetFit: 'contain',
    assetTransform: { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0, clipToBounds: true },
    assetAlphaCollisionEnabled: true,
    textureKey: defaultVisual.worldTextureKey,
    isDefaultItemAsset: true,
  };
}
